import { assertReadingProgress, type ReadingProgress, type ReaderStateStore } from '@tanko/shared'
import { db, type StoredBookmark } from './schema'

/**
 * 裝置上的閱讀狀態。
 *
 * 已註冊的作品以雲端目錄項為真相來源，這一份是它的快取；未註冊的作品只有這一份。
 * 兩種情況寫入都會標記 needsPush，因為寫的當下不見得知道（也不該在這一層決定）
 * 推送有沒有成功——清掉標記是推送那一層的事。
 */

const toBookmark = ({ comicId: _comicId, needsPush: _needsPush, deleted: _deleted, ...bookmark }: StoredBookmark) => bookmark

export function localReadingStore(comicId: string, sourcePageCount?: number): ReaderStateStore {
  async function comic() {
    const found = await db.comics.get(comicId)
    if (!found) throw new Error('作品已被移除')
    return found
  }
  return {
    load: () => db.transaction('r', [db.comics, db.readingProgress, db.bookmarks], async () => {
      const item = await comic()
      const [stored, bookmarks] = await Promise.all([
        db.readingProgress.get(comicId), db.bookmarks.where('comicId').equals(comicId).sortBy('createdAt'),
      ])
      return {
        progress: stored ? { page: stored.page, offset: stored.offset, updatedAt: stored.updatedAt } : null,
        // tombstone 只是還沒推出去的刪除紀錄，不是一個還在的書籤。
        bookmarks: bookmarks.filter(entry => !entry.deleted).map(toBookmark),
        coverPage: item.coverPage,
      }
    }),
    saveProgress: progress => db.transaction('rw', [db.comics, db.readingProgress], async () => {
      const item = await comic()
      assertReadingProgress(progress, sourcePageCount ?? item.pageCount)
      const previous = await db.readingProgress.get(comicId)
      if (!previous || progress.updatedAt >= previous.updatedAt) await db.readingProgress.put({ ...progress, comicId, needsPush: 1 })
    }),
    putBookmark: bookmark => db.transaction('rw', [db.comics, db.bookmarks], async () => {
      const item = await comic()
      assertReadingProgress(bookmark, sourcePageCount ?? item.pageCount)
      if (!bookmark.id || !bookmark.label.trim() || !Number.isSafeInteger(bookmark.createdAt) || bookmark.createdAt < 0) {
        throw new Error('請填寫有效的書籤名稱與建立時間')
      }
      await db.bookmarks.put({ ...bookmark, label: bookmark.label.trim(), comicId, needsPush: 1, deleted: 0 })
    }),
    /**
     * 刪除留下 tombstone 而不是直接移除該列：推送失敗時若本地也沒了，這次刪除就沒有
     * 任何紀錄，下次載入會把它從雲端讀回來。真正移除是推送之後的事。
     *
     * 一律留 tombstone，不以 needsPush 判斷「有沒有雲端副本要刪」——那個旗標講的是
     * 目前這一版送出去了沒有，而一個改過名、改名沒推成功的書籤，它的前一版早就在雲端了。
     */
    deleteBookmark: id => db.transaction('rw', [db.comics, db.bookmarks], async () => {
      await comic()
      const existing = await db.bookmarks.get([comicId, id])
      if (!existing || existing.deleted) return
      await db.bookmarks.put({ ...existing, deleted: 1, updatedAt: Date.now(), needsPush: 1 })
    }),
    setCover: async (page, thumbnail) => {
      if (!thumbnail.blob.size || thumbnail.blob.type !== 'image/webp') throw new Error('封面縮圖不是有效的 WebP')
      // 原圖比要求寬度還窄時 width 會等於原圖寬度，這是允許的，照實記錄。
      return db.transaction('rw', [db.comics, db.coverThumbnails], async () => {
        const item = await comic()
        assertReadingProgress({ page, offset: 0, updatedAt: 0 }, sourcePageCount ?? item.pageCount)
        await db.coverThumbnails.put({ comicId, page, blob: thumbnail.blob, width: thumbnail.width })
        await db.comics.update(comicId, { coverPage: page })
        return page
      })
    },
    clearCover: () => db.transaction('rw', [db.comics, db.coverThumbnails], async () => {
      await comic()
      await db.coverThumbnails.delete(comicId)
      await db.comics.update(comicId, { coverPage: 0 })
      return 0
    }),
  }
}

/** 本地還沒推出去的刪除。合併雲端狀態時要用它壓過雲端那一份。 */
export async function localTombstones(comicId: string): Promise<string[]> {
  const rows = await db.bookmarks.where('comicId').equals(comicId).toArray()
  return rows.filter(row => row.deleted).map(row => row.id)
}

/**
 * 推送完成之後清掉標記。
 *
 * 「完成」包含被伺服器以較新狀態拒絕——重送同一筆結果相同。tombstone 推出去之後就沒有
 * 保留的理由，直接移除；留著只會讓每次載入都多濾一次。
 */
export async function clearProgressPush(comicId: string): Promise<void> {
  await db.readingProgress.update(comicId, { needsPush: 0 })
}

export async function clearBookmarkPush(comicId: string, id: string): Promise<void> {
  await db.transaction('rw', [db.bookmarks], async () => {
    const existing = await db.bookmarks.get([comicId, id])
    if (!existing) return
    if (existing.deleted) { await db.bookmarks.delete([comicId, id]); return }
    await db.bookmarks.update([comicId, id], { needsPush: 0 })
  })
}

/** 一本作品還沒送出去的閱讀狀態，tombstone 也在裡面——刪除同樣要推。 */
export type UnpushedReadingState = { progress: ReadingProgress | null; bookmarks: StoredBookmark[] }

export async function unpushedReadingState(comicId: string): Promise<UnpushedReadingState> {
  const [progress, bookmarks] = await Promise.all([
    db.readingProgress.get(comicId),
    db.bookmarks.where('comicId').equals(comicId).sortBy('createdAt'),
  ])
  return {
    progress: progress?.needsPush ? { page: progress.page, offset: progress.offset, updatedAt: progress.updatedAt } : null,
    bookmarks: bookmarks.filter(entry => entry.needsPush),
  }
}

/** 有東西待推送的作品。兩張表各自查 needsPush 索引，不掃全表。 */
export async function comicsWithUnpushedReadingState(): Promise<string[]> {
  const [progress, bookmarks] = await Promise.all([
    db.readingProgress.where('needsPush').equals(1).primaryKeys(),
    db.bookmarks.where('needsPush').equals(1).toArray(),
  ])
  return [...new Set([...progress, ...bookmarks.map(entry => entry.comicId)])]
}
